import { useState, useRef } from 'react';
import { adminApi, clubsApi, championshipsApi } from '../../../lib/api';
import { toast } from 'sonner';

export const useClubsActions = ({ isItalian, users, championships, clubs, setClubs, fetchUsers, refreshUser }) => {
  const [clubSearchQuery, setClubSearchQuery] = useState('');

  // Delete
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clubToDelete, setClubToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Edit
  const [showEditClubDialog, setShowEditClubDialog] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [editClubForm, setEditClubForm] = useState({ name: '' });
  const [savingClub, setSavingClub] = useState(false);
  const editClubLogoRef = useRef(null);

  // Roster
  const [showRosterDialog, setShowRosterDialog] = useState(false);
  const [selectedClubRoster, setSelectedClubRoster] = useState(null);
  const [clubMembers, setClubMembers] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);

  // Create
  const [showCreateClubDialog, setShowCreateClubDialog] = useState(false);
  const [createClubForm, setCreateClubForm] = useState({ name: '', captainId: '', championshipId: '' });
  const [creatingClub, setCreatingClub] = useState(false);
  const [availableCaptains, setAvailableCaptains] = useState([]);

  // Add member
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [addMemberClub, setAddMemberClub] = useState(null);
  const [addMemberUserId, setAddMemberUserId] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [usersWithoutClub, setUsersWithoutClub] = useState([]);

  // --- Roster ---
  const handleViewRoster = async (club) => {
    setSelectedClubRoster(club);
    setShowRosterDialog(true);
    setLoadingRoster(true);
    try {
      const res = await clubsApi.getMembers(club.club_id);
      setClubMembers(res.data || []);
    } catch (error) {
      console.error('Error loading roster:', error);
      toast.error(isItalian ? 'Errore nel caricamento roster' : 'Error loading roster');
      setClubMembers([]);
    } finally {
      setLoadingRoster(false);
    }
  };

  const handleAssignRole = async (userId, roleType) => {
    if (!selectedClubRoster) return;
    try {
      const res = await clubsApi.assignRole(selectedClubRoster.club_id, userId, roleType);
      toast.success(res.data.message);
      const membersRes = await clubsApi.getMembers(selectedClubRoster.club_id);
      setClubMembers(membersRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  // --- Create ---
  const handleOpenCreateClub = () => {
    const available = users.filter(u => !u.club_id && u.role !== 'super_admin');
    setAvailableCaptains(available);
    setCreateClubForm({ name: '', captainId: '', championshipId: '' });
    setShowCreateClubDialog(true);
  };

  const handleCreateClub = async () => {
    if (!createClubForm.name.trim()) { toast.error(isItalian ? 'Inserisci il nome del club' : 'Enter club name'); return; }
    if (!createClubForm.captainId) { toast.error(isItalian ? 'Seleziona un capitano' : 'Select a captain'); return; }

    setCreatingClub(true);
    try {
      const clubRes = await adminApi.createClub({ name: createClubForm.name, captain_id: createClubForm.captainId });
      const newClub = clubRes.data;
      if (createClubForm.championshipId && newClub.club_id) {
        try {
          await championshipsApi.addClub(createClubForm.championshipId, newClub.club_id);
          toast.success(isItalian ? 'Club creato e aggiunto al campionato!' : 'Club created and added to championship!');
        } catch (champError) {
          toast.success(isItalian ? 'Club creato! Errore nell\'aggiunta al campionato.' : 'Club created! Error adding to championship.');
        }
      } else {
        toast.success(isItalian ? 'Club creato con successo!' : 'Club created successfully!');
      }
      const [clubsRes] = await Promise.all([clubsApi.list({ include_logo: false })]);
      setClubs(clubsRes.data || []);
      await fetchUsers();
      setShowCreateClubDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore nella creazione del club' : 'Error creating club'));
    } finally {
      setCreatingClub(false);
    }
  };

  // --- Edit ---
  const handleEditClub = (club) => {
    setEditingClub(club); setEditClubForm({ name: club.name }); setShowEditClubDialog(true);
  };

  const handleSaveClub = async () => {
    if (!editingClub) return;
    setSavingClub(true);
    try {
      await clubsApi.update(editingClub.club_id, { name: editClubForm.name });
      toast.success(isItalian ? 'Club aggiornato!' : 'Club updated!');
      setShowEditClubDialog(false);
      const clubsRes = await clubsApi.list();
      setClubs(clubsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    } finally { setSavingClub(false); }
  };

  const handleUploadClubLogo = async (e) => {
    const file = e.target.files[0];
    if (!file || !editingClub) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) { toast.error(isItalian ? 'Formato non supportato' : 'Unsupported format'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error(isItalian ? 'File troppo grande (max 2MB)' : 'File too large (max 2MB)'); return; }
    setSavingClub(true);
    try {
      const res = await clubsApi.uploadLogo(editingClub.club_id, file);
      toast.success(isItalian ? 'Logo aggiornato!' : 'Logo updated!');
      setEditingClub(prev => ({ ...prev, logo: res.data.logo }));
      setClubs(prev => prev.map(c => c.club_id === editingClub.club_id ? { ...c, logo: res.data.logo } : c));
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore upload' : 'Upload error'));
    } finally {
      setSavingClub(false);
      if (editClubLogoRef.current) editClubLogoRef.current.value = '';
    }
  };

  // --- Delete ---
  const handleDeleteClub = async (clubId, clubName) => {
    setClubToDelete({ id: clubId, name: clubName });
    setShowDeleteDialog(true);
  };

  const confirmDeleteClub = async () => {
    if (!clubToDelete) return;
    setDeleting(true);
    try {
      await clubsApi.delete(clubToDelete.id);
      setClubs(prev => prev.filter(c => c.club_id !== clubToDelete.id));
      toast.success(isItalian ? `Club "${clubToDelete.name}" eliminato!` : `Club "${clubToDelete.name}" deleted!`);
      await fetchUsers();
      await refreshUser();
      setShowDeleteDialog(false); setClubToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore eliminazione' : 'Delete error'));
    } finally {
      setDeleting(false);
    }
  };

  // --- Add Member ---
  const handleOpenAddMember = async (club) => {
    setAddMemberClub(club);
    setAddMemberUserId('');
    try {
      const res = await adminApi.getUsers({ skip: 0, limit: 500, search: '' });
      const data = res.data?.users || res.data || [];
      setUsersWithoutClub(data.filter(u => !u.club_id && u.role !== 'super_admin'));
    } catch (err) {
      setUsersWithoutClub([]);
    }
    setShowAddMemberDialog(true);
  };

  const handleAddMemberToClub = async () => {
    if (!addMemberClub || !addMemberUserId) return;
    setAddingMember(true);
    try {
      const res = await adminApi.addMemberToClub(addMemberClub.club_id, addMemberUserId);
      toast.success(res.data?.message || (isItalian ? 'Utente aggiunto al club!' : 'User added to club!'));
      setShowAddMemberDialog(false);
      setAddMemberClub(null);
      setAddMemberUserId('');
      const [clubsRes] = await Promise.all([clubsApi.list({ include_logo: false })]);
      setClubs(clubsRes.data || []);
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    } finally {
      setAddingMember(false);
    }
  };

  return {
    clubSearchQuery, setClubSearchQuery,

    handleOpenCreateClub, handleViewRoster, handleEditClub, handleDeleteClub,
    handleOpenAddMember,

    // Delete dialog
    showDeleteDialog, setShowDeleteDialog, clubToDelete, setClubToDelete, confirmDeleteClub, deleting,

    // Edit dialog
    showEditClubDialog, setShowEditClubDialog, editingClub, setEditingClub,
    editClubForm, setEditClubForm, savingClub, handleSaveClub, handleUploadClubLogo, editClubLogoRef,

    // Roster dialog
    showRosterDialog, setShowRosterDialog, selectedClubRoster, clubMembers, loadingRoster, handleAssignRole,

    // Create dialog
    showCreateClubDialog, setShowCreateClubDialog, createClubForm, setCreateClubForm,
    creatingClub, availableCaptains, handleCreateClub,

    // Add member dialog
    showAddMemberDialog, setShowAddMemberDialog, addMemberClub, addMemberUserId,
    setAddMemberUserId, addingMember, usersWithoutClub, handleAddMemberToClub,
  };
};
